import React, { useEffect, useState, useRef } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, Animated } from 'react-native';
import { Wine, Sparkles } from 'lucide-react-native';
// Importiamo il context di VinoLogic per abilitare la localizzazione a runtime su Web
import { useWineContext } from '../context/WineContext';

export default function RewardedAdModal({ visible, onClose, onReward }) {
  const { t } = useWineContext();
  const [timeLeft, setTimeLeft] = useState(10);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Animazione pulsante per dare un effetto "dinamico" al finto spot
  const pulseAnim = useRef(new Animated.Value(0.8)).current;

  useEffect(() => {
    if (visible) {
      setIsPlaying(true);
      setTimeLeft(10);

      // Avvia l'animazione ciclica del logo
      Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, { toValue: 1.1, duration: 1500, useNativeDriver: true }),
          Animated.timing(pulseAnim, { toValue: 0.8, duration: 1500, useNativeDriver: true })
        ])
      ).start();
    } else {
      setIsPlaying(false);
      pulseAnim.stopAnimation();
    }
  }, [visible]);

  // Gestione del Timer a scalare
  useEffect(() => {
    let timer;
    if (visible && isPlaying && timeLeft > 0) {
      timer = setTimeout(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (visible && timeLeft === 0 && isPlaying) {
      // Il timer è arrivato a 0, diamo la ricompensa!
      setIsPlaying(false);
      onReward();
    }

    return () => clearTimeout(timer);
  }, [visible, isPlaying, timeLeft]);

  if (!visible) return null;

  return (
    <Modal visible={visible} transparent={false} animationType="slide" onRequestClose={onClose}>
      <View style={styles.modalBackground}>
        <View style={styles.webContainer}>
          
          <Text style={styles.timerText}>
            {isPlaying 
              ? t('components.rewardedAd.webEndingIn').replace('{time}', timeLeft) 
              : t('components.rewardedAd.webRewardIncoming')
            }
          </Text>

          {/* SPOT SIMULATO PREMIUM WEB */}
          <View style={styles.videoWrapper}>
            <View style={styles.mockAdContent}>
              <Animated.View style={{ transform: [{ scale: pulseAnim }], alignItems: 'center' }}>
                <Wine color="#fbbf24" size={64} />
                <Sparkles color="#fde68a" size={24} style={{ position: 'absolute', top: -10, right: -10 }} />
              </Animated.View>
              
              <Text style={styles.adTitle}>{t('components.rewardedAd.webPartnerTitle')}</Text>
              <Text style={styles.adSubtitle}>{t('components.rewardedAd.webPartnerSub')}</Text>
              
              <View style={styles.adBadge}>
                <Text style={styles.adBadgeText}>{t('components.rewardedAd.webSponsoredBadge')}</Text>
              </View>
            </View>

            {/* Barra di avanzamento stile YouTube/Instagram */}
            <View style={styles.progressBarBg}>
              <View style={[styles.progressBarFill, { width: `${(10 - timeLeft) * 10}%` }]} />
            </View>
          </View>

          <TouchableOpacity style={styles.closeBtn} onPress={onClose}>
            <Text style={styles.closeBtnText}>{t('components.rewardedAd.webCloseAction')}</Text>
          </TouchableOpacity>
          
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalBackground: { 
    flex: 1, 
    backgroundColor: '#0f172a', 
    justifyContent: 'center', 
    alignItems: 'center' 
  },
  webContainer: { 
    flex: 1, 
    justifyContent: 'center', 
    alignItems: 'center', 
    width: '100%',
    padding: 20
  },
  timerText: { 
    color: '#e2e8f0', 
    fontSize: 16, 
    fontWeight: 'bold',
    marginBottom: 20,
    textAlign: 'center' 
  },
  videoWrapper: { 
    width: '100%', 
    maxWidth: 640, 
    aspectRatio: 16/9, 
    backgroundColor: '#1e1b4b', 
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0px 10px 30px rgba(0,0,0,0.5)',
    borderWidth: 1,
    borderColor: '#3730a3',
    justifyContent: 'space-between'
  },
  mockAdContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20
  },
  adTitle: {
    color: '#fbbf24', 
    fontSize: 24,
    fontWeight: 'bold',
    marginTop: 20,
    letterSpacing: 1
  },
  adSubtitle: {
    color: '#c7d2fe',
    fontSize: 14,
    marginTop: 8,
    textAlign: 'center'
  },
  adBadge: {
    position: 'absolute',
    top: 20,
    left: 20,
    backgroundColor: 'rgba(0,0,0,0.5)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)'
  },
  adBadgeText: {
    color: '#94a3b8',
    fontSize: 10,
    fontWeight: 'bold',
    textTransform: 'uppercase'
  },
  progressBarBg: {
    height: 6,
    backgroundColor: 'rgba(255,255,255,0.1)',
    width: '100%'
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: '#fbbf24' 
  },
  closeBtn: { 
    marginTop: 30, 
    padding: 12 
  },
  closeBtnText: {
    color: '#94a3b8', 
    fontSize: 16, 
    textDecorationLine: 'underline'
  }
});